/**
 * The find panel (decision 41; part 19a log, tables F and Y): a search landmark under the toolbar while it is open, with
 * the query, its options and scope, the replacement, the moves and the replaces, and what the search found. The writer
 * goes on typing in the text beside it; Escape or Close puts it away with the focus back in the text.
 */
import { createEffect, createSignal, on, onCleanup } from 'solid-js';
import type { FindNews, FindStatus } from '../find/controller.ts';
import { focusOn } from './announce.ts';
import { el, when } from '../ui/dom.ts';
import type { Child, On } from '../ui/dom.ts';
import Button from '../ui/Button.ts';
import Checkbox from '../ui/Checkbox.ts';
import IconButton from '../ui/IconButton.ts';
import RadioGroup from '../ui/RadioGroup.ts';
import { component } from '../ui/solid.ts';
import TextField from '../ui/TextField.ts';
import type { Shell } from './shell.ts';

/** Y: how long the status must stand before it is said, so typing a query isn't read out search by search (ms). */
export const SETTLE_MS = 1_000;

/** What the status line says about the search (table F, F13). */
export function findText(shell: Pick<Shell, 't'>, status: FindStatus): string {
  const { t } = shell;
  switch (status.kind) {
    case 'idle':
      return '';
    case 'searching':
      return t('find.searching');
    case 'refused':
      return status.problem === 'invalid' ? t('find.invalid') : t('find.tooLong');
    case 'stopped':
      return t('find.stopped');
    case 'failed':
      return t('find.failed');
    case 'found': {
      const { counts, scope, unreadable } = status.results;
      const count = counts.reduce((sum, each) => sum + each, 0);
      const chapters = counts.filter((each) => each > 0).length;
      const found = count === 0 ? t('find.none') : scope === 'chapter' ? t('find.matches', { count }) : chapters === 1 ? t('find.matchesInOne', { count }) : t('find.matchesIn', { count, chapters });
      return unreadable === 0 ? found : `${found} ${t('find.unreadable', { count: unreadable })}`;
    }
  }
}

/** What the last replace or undo said (F5–F7); empty when there is nothing to say (the workspace says it, or nothing happened). */
export function newsText(shell: Pick<Shell, 't'>, news: FindNews | null): string {
  const { t } = shell;
  if (news === null) return '';
  switch (news.kind) {
    case 'replacing':
      return t('find.replacing');
    case 'left-one':
      return t('find.leftOne');
    case 'undone': {
      const { outcome } = news;
      if (outcome.kind === 'undone') return outcome.kept === 0 ? t('find.undone', { count: outcome.restored }) : `${t('find.undone', { count: outcome.restored })} ${t('find.kept', { count: outcome.kept })}`;
      return outcome.kind === 'write-failed' ? t('find.undoFailed') : '';
    }
    case 'replaced-all': {
      const { outcome } = news;
      switch (outcome.kind) {
        case 'replaced': {
          const made = outcome.made === 0 ? t('find.nothingReplaced') : outcome.chapters === 1 ? t('find.replacedOne', { count: outcome.made }) : t('find.replaced', { count: outcome.made, chapters: outcome.chapters });
          return outcome.left === 0 ? made : `${made} ${t('find.left', { count: outcome.left })}`;
        }
        case 'stopped':
          return t('find.stopped');
        case 'refused':
          return outcome.problem === 'invalid' ? t('find.invalid') : t('find.tooLong');
        case 'changed':
          return t('find.changed');
        case 'failed':
        case 'write-failed':
          return t('find.replaceFailed');
        case 'not-editable':
        case 'save-failed':
        case 'stale':
          // The workspace says these itself (its notice, the other tab's screen).
          return '';
      }
    }
  }
}

/** The status line: the current match's place when there is one, else what the search found. */
function statusLine(shell: Shell): string {
  const { finder, t } = shell;
  const position = finder.position();
  const status = finder.status();
  const found = findText(shell, status);
  return position === null || status.kind !== 'found' ? found : `${t('find.position', { index: position.index, of: position.of })} ${found}`;
}

/**
 * Y: what the find announcer says: a move's place and a replace's news at once; the search's results only once they
 * have stood {@link SETTLE_MS}, so a query typed a letter at a time is said once.
 */
export function findNews(shell: Shell): () => string {
  const [settled, setSettled] = createSignal('');
  let timer: ReturnType<typeof setTimeout> | undefined;
  createEffect(
    on(
      () => findText(shell, shell.finder.status()),
      (text) => {
        clearTimeout(timer);
        timer = setTimeout(() => setSettled(text), SETTLE_MS);
      },
    ),
  );
  onCleanup(() => {
    clearTimeout(timer);
  });
  return () => {
    if (!shell.finder.open()) return '';
    const news = newsText(shell, shell.finder.news());
    if (news !== '') return news;
    const position = shell.finder.position();
    return position === null ? settled() : shell.t('find.position', { index: position.index, of: position.of });
  };
}

/** F1: opens the panel (leaving focus mode, where it can't show) with the focus in the query, its text selected. */
export function openFind(shell: Shell): void {
  const { workspace, finder } = shell;
  if (workspace.settings().focusMode) void workspace.updateSettings({ focusMode: false });
  finder.show();
  // The query selects its text as it takes the focus.
  queueMicrotask(() => {
    focusOn(shell, '#find-query');
  });
}

/** F8: closes the panel; the focus goes to the text (or where removing a control leaves it). */
export function closeFind(shell: Shell): void {
  shell.finder.hide();
  focusOn(shell, '.editor-host .cm-content');
}

/** Deep-fix r4: whether a key belongs to an input method's composition (its confirming Enter, Escape), which no key here acts on. */
function composing(event: KeyboardEvent): boolean {
  // Safari sends a composition's confirming Enter after `compositionend`, with `isComposing` false: its keyCode 229 is
  // the only sign, and the deprecated field is the only one carrying it.
  // eslint-disable-next-line @typescript-eslint/no-deprecated -- the one signal Safari gives (above)
  return event.isComposing || event.keyCode === 229;
}

/** F15: a move shows the text on a narrow screen showing the preview. */
function showText(shell: Shell): void {
  if (shell.mode() === 'narrow' && shell.ui.narrowPane() === 'preview') shell.ui.setNarrowPane('text');
}

/** The toolbar's Find (F1): opens the panel, or goes to its query when it is open. */
export function FindButton(shell: Shell): Child {
  return component(IconButton, {
    icon: 'search',
    label: shell.t('toolbar.find'),
    class: 'open-find',
    get 'aria-expanded'() {
      return shell.finder.open() ? 'true' : 'false';
    },
    'aria-controls': 'find-panel',
    onClick: () => {
      openFind(shell);
    },
  });
}

/** A labelled checkbox of the query's options (Q4–Q6; the library's Checkbox). */
function option(shell: Shell, id: string, label: string, key: 'matchCase' | 'wholeWords' | 'regex'): Child {
  return component(Checkbox, {
    id,
    label,
    get checked() {
      return shell.finder.query()[key];
    },
    onChange: (checked: boolean) => {
      shell.finder.setQuery({ [key]: checked });
    },
  });
}

/**
 * A button of the panel (the library's Button); `run` returns what it started, and a move shows the text (F15). While
 * `held` (a replace under way) it takes no press but keeps the focus (deep-fix r4: a focused button turned `disabled`
 * drops the focus to the page), as part 15a's BA5 buttons do.
 */
function action(shell: Shell, className: string, label: string, run: () => Promise<void>, disabled: () => boolean = () => false, held: () => boolean = () => false): Child {
  return component(Button, {
    size: 'sm',
    class: className,
    get disabled() {
      return disabled();
    },
    get 'aria-disabled'() {
      return held() ? 'true' : undefined;
    },
    onClick: () => {
      if (held()) return;
      void run().then(() => {
        showText(shell);
      });
    },
    children: label,
  });
}

/** Table F, Y: the panel, while open and the toolbar shows. */
export function FindPanel(shell: Shell): Child {
  const { t, finder, workspace } = shell;
  const cannotReplace = (): boolean => !workspace.writable() || finder.query().text === '';
  const busy = (): boolean => finder.busy();
  // F1: a writer's Escape in the panel closes it, before the page's own Escape (focus mode, the drawer) sees it.
  const keys = (event: KeyboardEvent): void => {
    if (event.key !== 'Escape' || event.defaultPrevented || composing(event)) return;
    event.preventDefault();
    closeFind(shell);
  };
  // Deep-fix r5: on a narrow screen the options, the scope and the replace controls wait behind a disclosure, so the
  // text keeps its room above the phone's keyboard; on a wide one they show.
  const more = (): Child[] => [
    el(
      'div',
      { class: 'find-options' },
      option(shell, 'find-match-case', t('find.matchCase'), 'matchCase'),
      option(shell, 'find-whole-words', t('find.wholeWords'), 'wholeWords'),
      option(shell, 'find-regex', t('find.regex'), 'regex'),
    ),
    component(RadioGroup, {
      class: 'find-scope',
      label: t('find.scope'),
      name: 'find-scope',
      orientation: 'horizontal',
      options: [
        { value: 'chapter', label: t('find.scope.chapter') },
        { value: 'book', label: t('find.scope.book') },
      ],
      get value() {
        return finder.scope();
      },
      onChange: (scope: string) => {
        finder.setScope(scope === 'chapter' ? 'chapter' : 'book');
      },
    }),
    component(TextField, {
      id: 'find-replacement',
      fieldClass: 'find-replace-field',
      label: t('find.replacement'),
      size: 'sm',
      autocomplete: 'off',
      maxLength: 1_000,
      get value() {
        return finder.replacement();
      },
      onChange: (value: string) => {
        finder.setReplacement(value);
      },
      onKeyDown: (event: KeyboardEvent) => {
        if (event.key !== 'Enter' || composing(event) || cannotReplace() || busy()) return;
        event.preventDefault();
        void finder.replace().then(() => {
          showText(shell);
        });
      },
    }),
    el(
      'div',
      { class: 'find-replaces' },
      action(shell, 'find-replace', t('find.replace'), () => finder.replace(), cannotReplace, busy),
      action(shell, 'find-replace-all', t('find.replaceAll'), () => finder.replaceAll(), cannotReplace, busy),
      when(
        () => workspace.replaceUndo() !== null,
        () =>
          action(
            shell,
            'find-undo',
            t('find.undo'),
            // A24: the button goes with the undo it made; the focus stays in the panel.
            () => finder.undoReplaceAll().then(() => { focusOn(shell, '#find-query'); }),
            () => !workspace.writable(),
            busy,
          ),
      ),
    ),
  ];
  return el(
    'section',
    { id: 'find-panel', class: 'find-panel', role: 'search', 'aria-label': t('find.label'), onKeyDown: keys },
    el(
      'div',
      { class: 'find-row' },
      component(TextField, {
        id: 'find-query',
        fieldClass: 'find-query',
        label: t('find.query'),
        size: 'sm',
        autocomplete: 'off',
        spellcheck: false,
        maxLength: 1_000,
        'aria-describedby': 'find-status',
        get value() {
          return finder.query().text;
        },
        onFocus: (event: On<FocusEvent, HTMLInputElement>) => {
          event.currentTarget.select();
        },
        onChange: (text: string) => {
          finder.setQuery({ text });
        },
        onKeyDown: (event: KeyboardEvent) => {
          if (event.key !== 'Enter' || composing(event)) return;
          event.preventDefault();
          void (event.shiftKey ? finder.previous() : finder.next()).then(() => {
            showText(shell);
          });
        },
      }),
      action(shell, 'find-previous', t('find.previous'), () => finder.previous()),
      action(shell, 'find-next', t('find.next'), () => finder.next()),
      component(Button, {
        variant: 'ghost',
        size: 'sm',
        class: 'find-close',
        onClick: () => {
          closeFind(shell);
        },
        children: t('find.close'),
      }),
    ),
    el('p', { id: 'find-status', class: 'find-status' }, () => statusLine(shell)),
    el('p', { class: 'find-news' }, () => newsText(shell, finder.news())),
    () => {
      const body = el('div', { class: 'find-more-body' }, ...more());
      return shell.mode() === 'wide' ? body : el('details', { class: 'find-more' }, el('summary', {}, t('find.more')), body);
    },
  );
}

/** F8, A24: the panel closing by any other way (focus mode, a narrow layout) leaves the highlights off. */
export function followFind(shell: Shell, shown: () => boolean): void {
  createEffect(
    on(
      shown,
      // Not deferred: a deferred `on` never records the first value, so `before` would be undefined at the first change.
      (now, before) => {
        if (before === true && !now && shell.finder.open()) shell.finder.hide();
      },
    ),
  );
}
